import React from 'react';
import { Badge, Skeleton } from 'antd';
import { FulfillmentBreakdownProps } from './types';
import { FULFILLMENT_ICONS } from './constants';
import { BreakdownGrid, BreakdownCard, BreakdownHeader, BreakdownTitle, BreakdownTitleText, BreakdownStats, BreakdownStatRow, BreakdownStatLabel, BreakdownStatValue, IconWithColor } from './Styles';
import { useTheme } from '../../../context/ThemeContext';
import { ThemeProvider } from 'styled-components';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
};

const FulfillmentBreakdown: React.FC<FulfillmentBreakdownProps> = ({ fulfillmentBreakdown, loading = false }) => {
  const { isDarkMode } = useTheme();
  const theme = { mode: isDarkMode ? 'dark' : 'light' } as const;

  if (loading || !fulfillmentBreakdown) {
    return (
      <ThemeProvider theme={theme}>
        <BreakdownGrid>
          {Array.from({ length: 2 }).map((_, index) => (
            <BreakdownCard key={index} variant={index === 0 ? 'orange' : 'purple'}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </BreakdownCard>
          ))}
        </BreakdownGrid>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <BreakdownGrid>
        <BreakdownCard variant="orange">
          <BreakdownHeader>
            <BreakdownTitle>
              <IconWithColor icon={FULFILLMENT_ICONS.pickup} iconcolor="#fa8c16" />
              <BreakdownTitleText>Pickup Orders</BreakdownTitleText>
            </BreakdownTitle>
            <Badge count={fulfillmentBreakdown.pickup.orderCount || 0} showZero color="#fa8c16" />
          </BreakdownHeader>
          <BreakdownStats>
            <BreakdownStatRow>
              <BreakdownStatLabel>Revenue:</BreakdownStatLabel>
              <BreakdownStatValue>{formatCurrency(fulfillmentBreakdown.pickup.revenue)}</BreakdownStatValue>
            </BreakdownStatRow>
            <BreakdownStatRow>
              <BreakdownStatLabel>Fees:</BreakdownStatLabel>
              <BreakdownStatValue color="#ff4d4f">{formatCurrency(fulfillmentBreakdown.pickup.fees)}</BreakdownStatValue>
            </BreakdownStatRow>
            <BreakdownStatRow>
              <BreakdownStatLabel>Net:</BreakdownStatLabel>
              <BreakdownStatValue color="#52c41a">{formatCurrency(fulfillmentBreakdown.pickup.netEarnings)}</BreakdownStatValue>
            </BreakdownStatRow>
          </BreakdownStats>
        </BreakdownCard>

        <BreakdownCard variant="purple">
          <BreakdownHeader>
            <BreakdownTitle>
              <IconWithColor icon={FULFILLMENT_ICONS.delivery} iconcolor="#722ed1" />
              <BreakdownTitleText>Delivery Orders</BreakdownTitleText>
            </BreakdownTitle>
            <Badge count={fulfillmentBreakdown.delivery.orderCount || 0} showZero color="#722ed1" />
          </BreakdownHeader>
          <BreakdownStats>
            <BreakdownStatRow>
              <BreakdownStatLabel>Revenue:</BreakdownStatLabel>
              <BreakdownStatValue>{formatCurrency(fulfillmentBreakdown.delivery.revenue)}</BreakdownStatValue>
            </BreakdownStatRow>
            <BreakdownStatRow>
              <BreakdownStatLabel>Fees:</BreakdownStatLabel>
              <BreakdownStatValue color="#ff4d4f">{formatCurrency(fulfillmentBreakdown.delivery.fees)}</BreakdownStatValue>
            </BreakdownStatRow>
            <BreakdownStatRow>
              <BreakdownStatLabel>Net:</BreakdownStatLabel>
              <BreakdownStatValue color="#52c41a">{formatCurrency(fulfillmentBreakdown.delivery.netEarnings)}</BreakdownStatValue>
            </BreakdownStatRow>
          </BreakdownStats>
        </BreakdownCard>
      </BreakdownGrid>
    </ThemeProvider>
  );
};

export default FulfillmentBreakdown;
